import type { MouseEvent } from 'react'
import { useState } from 'react'
import { AskBennyButton } from '@/components/assistant/AskBennyButton'
import { TrialChip } from '@/components/billing/TrialChip'
import { RecordButton } from '@/components/desktop/RecordButton'
import { PendingWritesIndicator } from '@/components/layout/PendingWritesIndicator'
import { TimeEntryWidget } from '@/components/time-entry'
import { useSidebar } from '@/components/ui/sidebar'
import { Skeleton } from '@/components/ui/skeleton'
import { useIsMobile, useIsNarrow } from '@/hooks/use-mobile'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { cn } from '@/lib/utils'
import { isTauri } from '@/lib/utils/tauri'
import { isWebRecordingSupported } from '@/lib/utils/web-recorder'

import { CustomSidebarTrigger } from './AppSidebar'
import {
  SidebarThemedChromeContext,
  TitleBarIsland,
  topBarTextClasses,
  useTitleBarTextMode,
} from './floating-top-bar'
import { NotificationButton } from './NotificationButton'
import { OmniSearch } from './OmniSearch'
import { OrganizationSwitcher } from './OrganizationSwitcher'
import { UserMenu } from './UserMenu'

interface HeaderProps {
  onOpenChange?: (open: boolean) => void
  /**
   * `floating`: the work shell's floating navigation style (web, desktop
   * widths). The header leaves the page card and sits on the tinted ground
   * as pill islands, like the desktop title bar. Personal spaces and phones
   * keep the docked bar.
   */
  variant?: 'docked' | 'floating'
}

export function Header({ onOpenChange, variant = 'docked' }: HeaderProps) {
  const isMobile = useIsMobile()
  const isDesktopApp = isTauri() && !isMobile
  // The desktop app renders its title bar at the MainLayout level instead.
  if (isDesktopApp) return null
  if (variant === 'floating' && !isMobile)
    return <FloatingHeader onOpenChange={onOpenChange} />
  return <DockedHeader onOpenChange={onOpenChange} />
}

function DockedHeader({ onOpenChange }: Pick<HeaderProps, 'onOpenChange'>) {
  const isMobile = useIsMobile()
  const isNarrow = useIsNarrow()
  const { state } = useSidebar()
  const { isLoading: isLoadingFeatureFlags } = useFeatureFlags([
    'MEETING_NOTES',
  ])
  // RecordButton hides itself until MEETING_NOTES resolves, so on a cold boot
  // it used to pop in and nudge the header's centre. Hold its slot while the
  // flags are in flight — but only where recording could appear at all.
  const isRecordSlotPending = isLoadingFeatureFlags && isWebRecordingSupported()
  const isSidebarCollapsed = state === 'collapsed'
  // The collapsed search anchors itself right (ml-auto), so the row only
  // spans the header while the field is open; otherwise it hugs its content
  // and the Ask Benny pill sits directly left of the search button.
  const [isSearchExpanded, setIsSearchExpanded] = useState(false)

  // Regular web/mobile header - white in light mode, black in dark mode
  return (
    <header
      className={cn(
        'sticky top-0 z-30 w-full transition-colors duration-300',
        'bg-white dark:bg-[hsl(var(--cast-iron-skillet))]',
        'border-b border-border/50',
      )}
    >
      <div
        className={cn(
          'flex w-full flex-wrap items-center gap-y-2 px-4 py-2 sm:h-14 sm:flex-nowrap sm:py-0',
        )}
      >
        {/* Left Side - Navigation & Time Entry */}
        <div className="order-1 flex flex-shrink-0 items-center gap-4">
          <div className="flex transition-transform duration-200 ease-out">
            <CustomSidebarTrigger className="flex" />
          </div>

          {/* Time Entry Widget + Meeting Recorder */}
          <div className="hidden items-center gap-2 sm:flex">
            <TimeEntryWidget />
            {isRecordSlotPending ? (
              <Skeleton className="h-8 w-[104px] rounded-lg" />
            ) : (
              <RecordButton />
            )}
          </div>
        </div>

        {/* Center - OmniSearch + Benny. Search sizing follows the same sm
            breakpoint as this row (useIsNarrow): a full-width field only
            where it gets its own row, otherwise the collapsed button. */}
        <div className="order-3 flex w-full min-w-0 flex-1 items-center justify-center sm:order-2 sm:justify-end sm:px-2">
          <div
            className={cn(
              'flex items-center gap-2 sm:justify-end',
              isNarrow || isSearchExpanded ? 'w-full sm:max-w-xl' : 'w-auto',
            )}
          >
            <TrialChip />
            {/* The one Benny door on web: popover under the pill, Expand
                hands the thread to Conversations. Label folds on phones. */}
            <AskBennyButton labelClassName="max-lg:hidden" />
            {/* Desktop widths: collapses to a button on the right until used.
                Mobile keeps the full field on its own row. */}
            <OmniSearch
              className="w-full"
              collapsible={!isNarrow}
              onExpandedChange={setIsSearchExpanded}
            />
          </div>
        </div>

        {/* Right Side - Organization & User */}
        <div className="order-2 flex flex-shrink-0 items-center gap-2 sm:order-3">
          {/* Utility Actions */}
          {/* Appearance lives in Settings → Account settings → Appearance. */}
          <div className="flex items-center gap-1">
            <NotificationButton />
          </div>
          {/* Renders only when a write is queued or the connection is down. */}
          <PendingWritesIndicator />
          {/* Organization Switcher */}
          <div
            className={cn(
              'hidden transition-opacity duration-200 sm:block',
              isSidebarCollapsed && !isMobile ? 'w-auto' : 'max-w-[200px]',
            )}
          >
            <OrganizationSwitcher className={isMobile ? 'w-full' : undefined} />
          </div>
          <div>
            <UserMenu
              onOpenChange={onOpenChange}
              getNavigationPath={path => path}
            />
          </div>
        </div>
      </div>
    </header>
  )
}

/**
 * The web header in the floating navigation style: the desktop title bar's
 * islands on the same tinted ground (`themed-titlebar floating-titlebar`
 * carries its colors and island shapes from theme.css), minus the window
 * controls the browser already provides.
 */
function FloatingHeader({ onOpenChange }: Pick<HeaderProps, 'onOpenChange'>) {
  const { useDarkText } = useTitleBarTextMode()
  const text = topBarTextClasses(useDarkText)
  const { isLoading: isLoadingFeatureFlags } = useFeatureFlags([
    'MEETING_NOTES',
  ])
  const isRecordSlotPending = isLoadingFeatureFlags && isWebRecordingSupported()
  const [isSearchExpanded, setIsSearchExpanded] = useState(false)

  // Collapsed, the search island is one round button: a click on its rim
  // opens the search too.
  const handleSearchIslandClick = (event: MouseEvent<HTMLDivElement>) => {
    event.currentTarget
      .querySelector<HTMLInputElement>('[data-omni-input] input')
      ?.focus()
  }

  return (
    <SidebarThemedChromeContext value={true}>
      <header
        className="themed-titlebar floating-titlebar relative z-30 w-full"
        // Style hooks shared with the desktop title bar (theme.css): the
        // ground tint and island glass. The web has no native glass.
        data-nav-style="floating"
        data-glass="false"
      >
        {/* The shell's 0.375rem gutter above (level with the rail's first
            island) and below (the gap to the page card), the desktop title
            bar's 52px; islands 0.75rem apart, as in the rail. */}
        <div className="flex h-[52px] w-full items-center gap-3 py-1.5">
          {/* Navigation view (open / closed / hybrid rail): a plain control on
              the ground beside the rail, not an island. */}
          <div className="ml-2 flex shrink-0 items-center">
            <CustomSidebarTrigger className={cn('h-8 w-8', text.iconButton)} />
          </div>

          <TitleBarIsland
            floating
            index={1}
            isDragRegion={false}
            className="floating-titlebar-tools"
            // The island widens when a timer starts and narrows when it stops.
            layout
          >
            <div className={cn('flex items-center', text.timer)}>
              <TimeEntryWidget />
            </div>
            {/* Hidden by theme.css when Record renders nothing. */}
            <div
              className={cn(
                'titlebar-tools-divider mx-0.5 h-5 w-px',
                text.divider,
              )}
            />
            <div className={cn('flex items-center', text.record)}>
              {isRecordSlotPending ? (
                <Skeleton className="h-7 w-[104px] rounded-full" />
              ) : (
                <RecordButton />
              )}
            </div>
          </TitleBarIsland>

          <div className="flex min-w-0 flex-1 items-center justify-end">
            <div className="flex w-full max-w-xl items-center justify-end gap-3">
              <TrialChip />
              {/* shrink-0: the expanded search takes the free width, never
                  Benny's. */}
              <TitleBarIsland
                floating
                index={2}
                isDragRegion={false}
                className="shrink-0"
              >
                <AskBennyButton labelClassName="max-lg:hidden" />
              </TitleBarIsland>
            </div>
          </div>

          <TitleBarIsland
            floating
            index={4}
            isDragRegion={false}
            // theme.css sizes these controls to match the other islands.
            className="floating-titlebar-account shrink-0 gap-1"
          >
            {/* Search leads the account island: collapsed, a 36×28
                capsule like the bell beside it; opened, the field grows
                inside the island. The input's own border and fill are
                cleared in theme.css. */}
            <div
              className={cn(
                'floating-titlebar-search duration-base ease-ios flex min-w-0 items-center rounded-full transition-[width]',
                isSearchExpanded
                  ? 'w-[min(22rem,32vw)]'
                  : 'floating-titlebar-search-collapsed w-9 cursor-pointer',
              )}
              onClick={isSearchExpanded ? undefined : handleSearchIslandClick}
            >
              <OmniSearch
                className="w-full"
                collapsible
                onExpandedChange={setIsSearchExpanded}
              />
            </div>
            <div className={cn('flex items-center gap-1', text.utilities)}>
              <NotificationButton />
            </div>
            {/* Renders only when a write is queued or the connection is down. */}
            <PendingWritesIndicator />
            <OrganizationSwitcher className="max-w-[180px]" />
            <UserMenu
              onOpenChange={onOpenChange}
              getNavigationPath={path => path}
            />
          </TitleBarIsland>
        </div>
      </header>
    </SidebarThemedChromeContext>
  )
}
